import { ReactNode, useEffect, useState, lazy, useCallback, useRef } from 'react';
import { CircularProgress, CssBaseline } from '@mui/material';
import { API_URL, fetchData } from 'src/common/config/TextMessage';
import { localStorageMethod } from 'src/common/config/FunctionHelper';
import ThemeProvider from 'src/theme/ThemeProvider';

const StatusCheck = lazy(() => import('src/content/pages/Status/StatusCheck'));

interface ApiHealthGuardProps {
  children: ReactNode;
  fallback?: ReactNode;
}

/**
 * Pings the API endpoint to verify if the backend service is reachable.
 */
const checkApiHealth = async (): Promise<boolean> => {
  try {
    const response = await fetch(API_URL, {
      method: 'GET',
      credentials: 'include'
    });

    return response?.status === 200;
  } catch (error) {
    console.error('Error fetching API health:', error);
    return false;
  }
};

/**
 * `ApiHealthGuard` verifies that the backend API is online and responding.
 * If the API is unreachable, it attempts retries and falls back to `<StatusCheck />`
 * when down, or displays a loading indicator while retrying.
 */
export default function ApiHealthGuard({
  children,
  fallback
}: ApiHealthGuardProps): JSX.Element | null {
  const [isApiUp, setIsApiUp] = useState<boolean>(true);
  const [isCheckingApi, setIsCheckingApi] = useState<boolean>(false);
  const [retryCount, setRetryCount] = useState<number>(0);
  const retryTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  const isApiDownStored = localStorageMethod('get', 'api_down') === 'true';

  const runHealthCheck = useCallback(async (currentRetry: number) => {
    const apiSuccess = await checkApiHealth();

    if (apiSuccess) {
      setIsApiUp(true);
      localStorageMethod('remove', 'api_down');
      setIsCheckingApi(false);
    } else if (currentRetry < 2) {
      setIsCheckingApi(true);
      const nextRetry = currentRetry + 1;
      setRetryCount(nextRetry);

      if (currentRetry === 1) {
        setIsApiUp(false);
      }

      if (retryTimeoutRef.current) {
        clearTimeout(retryTimeoutRef.current);
      }
      retryTimeoutRef.current = setTimeout(() => {
        runHealthCheck(nextRetry);
      }, 3000);
    } else {
      if (process.env.NODE_ENV === 'development') {
        setIsApiUp(true);
      } else {
        setIsApiUp(false);
      }
      setIsCheckingApi(false);
    }
  }, []);

  useEffect(() => {
    if (isApiDownStored) {
      runHealthCheck(retryCount);
    }
  }, [isApiDownStored, runHealthCheck, retryCount]);

  useEffect(() => {
    runHealthCheck(0);

    return () => {
      if (retryTimeoutRef.current) {
        clearTimeout(retryTimeoutRef.current);
      }
    };
  }, [runHealthCheck]);

  if (isCheckingApi) {
    return (
      <div
        style={{
          width: '100vw',
          height: '100vh',
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          alignItems: 'center',
          fontSize: '1.5rem',
          textAlign: 'center'
        }}
      >
        <CircularProgress size={50} sx={{ color: 'var(--color-primary)' }} />
        <div style={{ marginTop: '20px' }}>{fetchData}</div>
      </div>
    );
  }

  if (!isApiUp) {
    if (fallback) {
      return <>{fallback}</>;
    }
    return (
      <ThemeProvider>
        <CssBaseline />
        <StatusCheck />
      </ThemeProvider>
    );
  }

  return <>{children}</>;
}
